import { useEffect, useMemo, useState } from "react";
import {
  DoorOpen,
  LogIn,
  LogOut,
  Search,
  ShieldAlert,
  Ticket,
  UserCog,
} from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Loading, PageHeader } from "../../components/shared";

const STATUS_META = {
  APPROVAL_PENDING: { label: "Awaiting approval", cls: "bg-amber-100 text-amber-800" },
  APPROVED: { label: "Approved", cls: "bg-blue-100 text-blue-800" },
  REJECTED: { label: "Rejected", cls: "bg-rose-100 text-rose-800" },
  CHECKED_IN: { label: "Inside", cls: "bg-emerald-100 text-emerald-800" },
  CHECKED_OUT: { label: "Exited", cls: "bg-slate-100 text-slate-700" },
  CANCELLED: { label: "Cancelled", cls: "bg-slate-100 text-slate-700" },
};

const INCIDENT_SEVERITIES = ["LOW", "NORMAL", "HIGH", "CRITICAL"];
const INCIDENT_NEXT = {
  OPEN: ["UNDER_REVIEW", "RESOLVED", "CLOSED"],
  UNDER_REVIEW: ["OPEN", "RESOLVED", "CLOSED"],
  RESOLVED: ["UNDER_REVIEW", "CLOSED"],
  CLOSED: ["UNDER_REVIEW"],
};
const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

function StatusBadge({ status }) {
  const meta = STATUS_META[status] || { label: status, cls: "bg-slate-100 text-slate-700" };
  return (
    <span
      className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${meta.cls}`}
      data-testid="gate-status-badge"
    >
      {meta.label}
    </span>
  );
}

export default function GateSecurity() {
  const [data, setData] = useState(null);
  const [incidents, setIncidents] = useState([]);
  const [history, setHistory] = useState([]);
  const [walkIn, setWalkIn] = useState({
    gate_id: "",
    unit_id: "",
    visitor_type_id: "",
    visitor_name: "",
    purpose: "",
    vehicle_number: "",
  });
  const [token, setToken] = useState("");
  const [tokenPreview, setTokenPreview] = useState(null);
  const [tokenGate, setTokenGate] = useState("");
  const [recurringForm, setRecurringForm] = useState({
    name: "",
    mobile: "",
    type: "Maid",
    unit_ids: [],
    valid_from: "",
    valid_until: "",
    allowed_days: [],
    allowed_time_window: "",
  });
  const [recurringEntry, setRecurringEntry] = useState({});
  const [exitGate, setExitGate] = useState({});
  const [checkinGate, setCheckinGate] = useState({});
  const [incident, setIncident] = useState({
    gate_id: "",
    category: "Security",
    severity: "NORMAL",
    description: "",
  });
  const [filters, setFilters] = useState({
    gate_id: "",
    unit_id: "",
    visitor_type: "",
    status: "",
    kind: "",
    q: "",
    date_from: "",
    date_to: "",
  });

  const load = async () => {
    try {
      const response = await api.get("/society-gate/bootstrap");
      setData(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not load gate desk");
      setData({ gates: [], visitor_types: [], visits: [], units: [], recurring_persons: [] });
    }
  };
  const loadIncidents = async () => {
    try {
      const response = await api.get("/society-gate/incidents");
      setIncidents(response.data);
    } catch {
      setIncidents([]);
    }
  };
  useEffect(() => {
    load();
    loadIncidents();
  }, []);

  const run = async (call, message) => {
    try {
      await call();
      toast.success(message);
      load();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Gate action failed");
    }
  };

  const entryGates = useMemo(
    () => (data?.gates || []).filter((g) => g.active && g.entry_allowed),
    [data],
  );
  const exitGates = useMemo(
    () => (data?.gates || []).filter((g) => g.active && g.exit_allowed),
    [data],
  );
  const unitName = (id) =>
    (data?.units || []).find((u) => u.unit_id === id)?.unit_number || id;

  const validateToken = async () => {
    try {
      const res = await api.post("/society-gate/invitations/validate", { token });
      setTokenPreview(res.data);
    } catch (error) {
      setTokenPreview(null);
      toast.error(error.response?.data?.detail || "Invitation not found");
    }
  };
  const checkInToken = () =>
    run(async () => {
      await api.post("/society-gate/invitations/check-in", {
        token,
        gate_id: tokenGate,
      });
      setToken("");
      setTokenPreview(null);
      setTokenGate("");
    }, "Pre-approved visitor checked in");

  const createRecurring = () =>
    run(async () => {
      await api.post("/society-gate/recurring-persons", recurringForm);
      setRecurringForm({
        name: "",
        mobile: "",
        type: "Maid",
        unit_ids: [],
        valid_from: "",
        valid_until: "",
        allowed_days: [],
        allowed_time_window: "",
      });
    }, "Recurring worker added");

  const applyFilters = async () => {
    try {
      const params = Object.fromEntries(
        Object.entries(filters).filter(([, v]) => v),
      );
      const res = await api.get("/society-gate/history", { params });
      setHistory(res.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not load history");
    }
  };

  const updateIncident = async (id, payload, message) => {
    try {
      await api.patch(`/society-gate/incidents/${id}`, payload);
      toast.success(message);
      loadIncidents();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not update incident");
    }
  };

  if (!data) return <Loading />;
  const inside = data.visits.filter((v) => v.status === "CHECKED_IN");
  const pending = data.visits.filter((v) => v.status === "APPROVAL_PENDING");

  return (
    <div data-testid="gate-security-page" className="pb-16">
      <PageHeader
        title="Gate & Security Desk"
        subtitle="Software-first visitor verification, entry, exit, and incident records."
      />

      <div className="grid gap-3 sm:grid-cols-3" data-testid="gate-security-counts">
        <div className="border border-slate-200 bg-white p-4">
          <strong className="text-2xl">{pending.length}</strong>
          <p className="text-sm text-slate-600">Awaiting approval</p>
        </div>
        <div className="border border-slate-200 bg-white p-4">
          <strong className="text-2xl">{inside.length}</strong>
          <p className="text-sm text-slate-600">Currently inside</p>
        </div>
        <div className="border border-slate-200 bg-white p-4">
          <strong className="text-2xl">
            {data.visits.filter((v) => v.status === "CHECKED_OUT").length}
          </strong>
          <p className="text-sm text-slate-600">Recent exits</p>
        </div>
      </div>

      {/* Walk-in */}
      <section className="mt-6 border border-slate-200 bg-white p-5" data-testid="walk-in-form">
        <h2 className="font-display text-xl font-semibold">Walk-in visitor</h2>
        <div className="mt-4 grid gap-3 sm:grid-cols-2">
          {[
            ["gate_id", "Gate", entryGates, "gate_id", "name"],
            ["unit_id", "Destination unit", data.units, "unit_id", "unit_number"],
            ["visitor_type_id", "Visitor type", data.visitor_types, "visitor_type_id", "name"],
          ].map(([key, label, options, id, text]) => (
            <select
              className="rounded-md border border-slate-300 px-3 py-2"
              data-testid={`gate-${key}`}
              key={key}
              onChange={(e) => setWalkIn({ ...walkIn, [key]: e.target.value })}
              value={walkIn[key]}
            >
              <option value="">Select {label}</option>
              {options.map((item) => (
                <option key={item[id]} value={item[id]}>
                  {item[text]}
                </option>
              ))}
            </select>
          ))}
          <Input
            data-testid="gate-visitor-name"
            onChange={(e) => setWalkIn({ ...walkIn, visitor_name: e.target.value })}
            placeholder="Visitor name"
            value={walkIn.visitor_name}
          />
          <Input
            data-testid="gate-purpose"
            onChange={(e) => setWalkIn({ ...walkIn, purpose: e.target.value })}
            placeholder="Purpose"
            value={walkIn.purpose}
          />
          <Input
            data-testid="gate-vehicle"
            onChange={(e) => setWalkIn({ ...walkIn, vehicle_number: e.target.value })}
            placeholder="Vehicle number"
            value={walkIn.vehicle_number}
          />
        </div>
        <Button
          className="mt-4"
          data-testid="gate-create-visit"
          onClick={() =>
            run(() => api.post("/society-gate/visits", walkIn), "Visitor arrival recorded")
          }
        >
          <DoorOpen className="h-4 w-4" />
          Request entry
        </Button>
      </section>

      {/* Invitation token check-in */}
      <section
        className="mt-6 border border-slate-200 bg-white p-5"
        data-testid="invitation-checkin"
      >
        <h2 className="font-display text-xl font-semibold">Pre-approved visitor (token)</h2>
        <div className="mt-4 flex flex-wrap gap-3">
          <Input
            className="max-w-sm"
            data-testid="invitation-token-input"
            onChange={(e) => setToken(e.target.value)}
            placeholder="Paste invitation code"
            value={token}
          />
          <Button
            data-testid="invitation-validate"
            onClick={validateToken}
            variant="outline"
          >
            <Ticket className="h-4 w-4" />
            Validate
          </Button>
        </div>
        {tokenPreview && (
          <div
            className="mt-4 rounded-md border border-slate-200 bg-slate-50 p-4"
            data-testid="invitation-preview"
          >
            <div className="flex flex-wrap justify-between gap-2">
              <strong>{tokenPreview.visitor_name}</strong>
              <StatusBadge status={tokenPreview.computed_status} />
            </div>
            <p className="mt-1 text-sm text-slate-600">
              {tokenPreview.visitor_type} · Unit {tokenPreview.unit_number} ·{" "}
              {tokenPreview.multiple_entry ? "Multi-entry" : "Single entry"}
            </p>
            <p className="text-xs text-slate-500">
              Valid {tokenPreview.valid_from} → {tokenPreview.valid_until}
            </p>
            {tokenPreview.computed_status === "VALID" ? (
              <div className="mt-3 flex flex-wrap gap-3">
                <select
                  className="rounded-md border border-slate-300 px-3 py-2"
                  data-testid="invitation-gate"
                  onChange={(e) => setTokenGate(e.target.value)}
                  value={tokenGate}
                >
                  <option value="">Select entry gate</option>
                  {entryGates.map((g) => (
                    <option key={g.gate_id} value={g.gate_id}>
                      {g.name}
                    </option>
                  ))}
                </select>
                <Button
                  data-testid="invitation-checkin-btn"
                  disabled={!tokenGate}
                  onClick={checkInToken}
                >
                  <LogIn className="h-4 w-4" />
                  Check in
                </Button>
              </div>
            ) : (
              <p className="mt-2 text-sm font-semibold text-rose-700" data-testid="invitation-denied">
                Entry not allowed — invitation is {tokenPreview.computed_status.toLowerCase()}.
              </p>
            )}
          </div>
        )}
      </section>

      {/* Recurring workers */}
      <section
        className="mt-6 border border-slate-200 bg-white p-5"
        data-testid="recurring-section"
      >
        <h2 className="font-display text-xl font-semibold">Recurring workers</h2>
        <div className="mt-4 grid gap-3 rounded-md border border-dashed border-slate-300 p-4 sm:grid-cols-2">
          <Input
            data-testid="recurring-name"
            onChange={(e) => setRecurringForm({ ...recurringForm, name: e.target.value })}
            placeholder="Worker name"
            value={recurringForm.name}
          />
          <Input
            data-testid="recurring-mobile"
            onChange={(e) => setRecurringForm({ ...recurringForm, mobile: e.target.value })}
            placeholder="Mobile"
            value={recurringForm.mobile}
          />
          <Input
            data-testid="recurring-type"
            onChange={(e) => setRecurringForm({ ...recurringForm, type: e.target.value })}
            placeholder="Type (Maid, Cook...)"
            value={recurringForm.type}
          />
          <select
            className="rounded-md border border-slate-300 px-3 py-2"
            data-testid="recurring-unit"
            multiple
            onChange={(e) =>
              setRecurringForm({
                ...recurringForm,
                unit_ids: Array.from(e.target.selectedOptions, (o) => o.value),
              })
            }
            value={recurringForm.unit_ids}
          >
            {data.units.map((u) => (
              <option key={u.unit_id} value={u.unit_id}>
                {u.unit_number}
              </option>
            ))}
          </select>
          <label className="text-xs text-slate-500">
            Valid from
            <input
              className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2"
              data-testid="recurring-valid-from"
              onChange={(e) =>
                setRecurringForm({ ...recurringForm, valid_from: e.target.value })
              }
              type="date"
              value={recurringForm.valid_from}
            />
          </label>
          <label className="text-xs text-slate-500">
            Valid until
            <input
              className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2"
              data-testid="recurring-valid-until"
              onChange={(e) =>
                setRecurringForm({ ...recurringForm, valid_until: e.target.value })
              }
              type="date"
              value={recurringForm.valid_until}
            />
          </label>
          <div className="sm:col-span-2">
            <p className="text-xs text-slate-500">Allowed days</p>
            <div className="mt-1 flex flex-wrap gap-2">
              {WEEKDAYS.map((day) => (
                <button
                  className={`rounded-full border px-3 py-1 text-xs ${
                    recurringForm.allowed_days.includes(day)
                      ? "border-slate-900 bg-slate-900 text-white"
                      : "border-slate-300 text-slate-600"
                  }`}
                  data-testid={`recurring-day-${day}`}
                  key={day}
                  onClick={() =>
                    setRecurringForm({
                      ...recurringForm,
                      allowed_days: recurringForm.allowed_days.includes(day)
                        ? recurringForm.allowed_days.filter((d) => d !== day)
                        : [...recurringForm.allowed_days, day],
                    })
                  }
                  type="button"
                >
                  {day}
                </button>
              ))}
            </div>
          </div>
          <Input
            data-testid="recurring-time-window"
            onChange={(e) =>
              setRecurringForm({ ...recurringForm, allowed_time_window: e.target.value })
            }
            placeholder="Time window e.g. 08:00-11:00"
            value={recurringForm.allowed_time_window}
          />
          <Button data-testid="recurring-create" onClick={createRecurring} size="sm">
            <UserCog className="h-4 w-4" />
            Add worker
          </Button>
        </div>

        <div className="mt-4 space-y-2" data-testid="recurring-list">
          {(data.recurring_persons || []).map((person) => (
            <article
              className="border border-slate-200 p-4"
              data-testid={`recurring-row-${person.recurring_person_id}`}
              key={person.recurring_person_id}
            >
              <div className="flex flex-wrap justify-between gap-2">
                <strong>
                  {person.name} · {person.type}
                </strong>
                <span
                  className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${
                    person.status === "ACTIVE"
                      ? "bg-emerald-100 text-emerald-800"
                      : "bg-rose-100 text-rose-800"
                  }`}
                >
                  {person.status}
                </span>
              </div>
              <p className="mt-1 text-xs text-slate-500">
                Units: {person.unit_ids.map(unitName).join(", ")}
                {person.valid_from && ` · from ${person.valid_from}`}
                {person.valid_until && ` → ${person.valid_until}`}
                {person.allowed_days?.length ? ` · ${person.allowed_days.join(", ")}` : ""}
                {person.allowed_time_window ? ` · ${person.allowed_time_window}` : ""}
              </p>
              <div className="mt-3 flex flex-wrap items-center gap-2">
                <select
                  className="rounded-md border border-slate-300 px-2 py-1.5 text-sm"
                  data-testid={`recurring-entry-unit-${person.recurring_person_id}`}
                  onChange={(e) =>
                    setRecurringEntry({
                      ...recurringEntry,
                      [person.recurring_person_id]: {
                        ...recurringEntry[person.recurring_person_id],
                        unit_id: e.target.value,
                      },
                    })
                  }
                  value={recurringEntry[person.recurring_person_id]?.unit_id || ""}
                >
                  <option value="">Unit</option>
                  {person.unit_ids.map((id) => (
                    <option key={id} value={id}>
                      {unitName(id)}
                    </option>
                  ))}
                </select>
                <select
                  className="rounded-md border border-slate-300 px-2 py-1.5 text-sm"
                  data-testid={`recurring-entry-gate-${person.recurring_person_id}`}
                  onChange={(e) =>
                    setRecurringEntry({
                      ...recurringEntry,
                      [person.recurring_person_id]: {
                        ...recurringEntry[person.recurring_person_id],
                        gate_id: e.target.value,
                      },
                    })
                  }
                  value={recurringEntry[person.recurring_person_id]?.gate_id || ""}
                >
                  <option value="">Gate</option>
                  {entryGates.map((g) => (
                    <option key={g.gate_id} value={g.gate_id}>
                      {g.name}
                    </option>
                  ))}
                </select>
                <Button
                  data-testid={`recurring-checkin-${person.recurring_person_id}`}
                  onClick={() =>
                    run(
                      () =>
                        api.post(
                          `/society-gate/recurring-persons/${person.recurring_person_id}/check-in`,
                          recurringEntry[person.recurring_person_id] || {},
                        ),
                      "Worker checked in",
                    )
                  }
                  size="sm"
                >
                  <LogIn className="h-4 w-4" />
                  Check in
                </Button>
                <Button
                  data-testid={`recurring-toggle-${person.recurring_person_id}`}
                  onClick={() =>
                    run(
                      () =>
                        api.patch(
                          `/society-gate/recurring-persons/${person.recurring_person_id}`,
                          { status: person.status === "ACTIVE" ? "SUSPENDED" : "ACTIVE" },
                        ),
                      "Worker updated",
                    )
                  }
                  size="sm"
                  variant="outline"
                >
                  {person.status === "ACTIVE" ? "Suspend" : "Activate"}
                </Button>
              </div>
            </article>
          ))}
        </div>
      </section>

      {/* Currently inside */}
      <section className="mt-8" data-testid="currently-inside">
        <h2 className="font-display text-2xl font-semibold">Currently inside</h2>
        <div className="mt-3 space-y-2">
          {inside.length === 0 && (
            <p className="border border-dashed border-slate-300 bg-white p-4 text-sm text-slate-600">
              No active visitors inside.
            </p>
          )}
          {inside.map((visit) => (
            <article
              className="border border-slate-200 bg-white p-4"
              data-testid={`inside-row-${visit.visit_id}`}
              key={visit.visit_id}
            >
              <div className="flex flex-wrap justify-between gap-2">
                <strong>{visit.visitor_name}</strong>
                <StatusBadge status={visit.status} />
              </div>
              <p className="mt-1 text-sm text-slate-600">
                {visit.visitor_type} · Unit {visit.unit_number} ·{" "}
                {visit.entry_event?.gate_name || visit.arrival_gate_name}
                {visit.vehicle_number ? ` · ${visit.vehicle_number}` : ""}
              </p>
              <div className="mt-3 flex flex-wrap items-center gap-2">
                <select
                  className="rounded-md border border-slate-300 px-2 py-1.5 text-sm"
                  data-testid={`exit-gate-${visit.visit_id}`}
                  onChange={(e) =>
                    setExitGate({ ...exitGate, [visit.visit_id]: e.target.value })
                  }
                  value={exitGate[visit.visit_id] || ""}
                >
                  <option value="">Exit gate</option>
                  {exitGates.map((g) => (
                    <option key={g.gate_id} value={g.gate_id}>
                      {g.name}
                    </option>
                  ))}
                </select>
                <Button
                  data-testid={`gate-checkout-${visit.visit_id}`}
                  disabled={!exitGate[visit.visit_id]}
                  onClick={() =>
                    run(
                      () =>
                        api.post(`/society-gate/visits/${visit.visit_id}/check-out`, {
                          gate_id: exitGate[visit.visit_id],
                        }),
                      "Visitor checked out",
                    )
                  }
                  size="sm"
                  variant="outline"
                >
                  <LogOut className="h-4 w-4" />
                  Check out
                </Button>
              </div>
            </article>
          ))}
        </div>
      </section>

      {/* Approvals / activity needing check-in */}
      <section className="mt-8" data-testid="gate-visit-list">
        <h2 className="font-display text-2xl font-semibold">Gate activity</h2>
        <div className="mt-3 space-y-2">
          {data.visits.slice(0, 50).map((visit) => (
            <article className="border border-slate-200 bg-white p-4" key={visit.visit_id}>
              <div className="flex flex-wrap justify-between gap-2">
                <strong>{visit.visitor_name}</strong>
                <StatusBadge status={visit.status} />
              </div>
              <p className="mt-1 text-sm text-slate-600">
                {visit.visitor_type} · Unit {visit.unit_number} · {visit.arrival_gate_name}
              </p>
              {visit.status === "APPROVED" && (
                <div className="mt-3 flex flex-wrap items-center gap-2">
                  <select
                    className="rounded-md border border-slate-300 px-2 py-1.5 text-sm"
                    data-testid={`checkin-gate-${visit.visit_id}`}
                    onChange={(e) =>
                      setCheckinGate({ ...checkinGate, [visit.visit_id]: e.target.value })
                    }
                    value={checkinGate[visit.visit_id] || visit.arrival_gate_id}
                  >
                    {entryGates.map((g) => (
                      <option key={g.gate_id} value={g.gate_id}>
                        {g.name}
                      </option>
                    ))}
                  </select>
                  <Button
                    data-testid={`gate-checkin-${visit.visit_id}`}
                    onClick={() =>
                      run(
                        () =>
                          api.post(`/society-gate/visits/${visit.visit_id}/check-in`, {
                            gate_id: checkinGate[visit.visit_id] || visit.arrival_gate_id,
                          }),
                        "Visitor checked in",
                      )
                    }
                    size="sm"
                  >
                    <LogIn className="h-4 w-4" />
                    Check in
                  </Button>
                </div>
              )}
            </article>
          ))}
        </div>
      </section>

      {/* Gate history with combined filters */}
      <section
        className="mt-8 border border-slate-200 bg-white p-5"
        data-testid="gate-history"
      >
        <h2 className="font-display text-xl font-semibold">Gate history & reports</h2>
        <div className="mt-3 grid gap-3 sm:grid-cols-3">
          <select
            className="rounded-md border border-slate-300 px-3 py-2"
            data-testid="history-gate"
            onChange={(e) => setFilters({ ...filters, gate_id: e.target.value })}
            value={filters.gate_id}
          >
            <option value="">All gates</option>
            {data.gates.map((g) => (
              <option key={g.gate_id} value={g.gate_id}>
                {g.name}
              </option>
            ))}
          </select>
          <select
            className="rounded-md border border-slate-300 px-3 py-2"
            data-testid="history-unit"
            onChange={(e) => setFilters({ ...filters, unit_id: e.target.value })}
            value={filters.unit_id}
          >
            <option value="">All units</option>
            {data.units.map((u) => (
              <option key={u.unit_id} value={u.unit_id}>
                {u.unit_number}
              </option>
            ))}
          </select>
          <select
            className="rounded-md border border-slate-300 px-3 py-2"
            data-testid="history-status"
            onChange={(e) => setFilters({ ...filters, status: e.target.value })}
            value={filters.status}
          >
            <option value="">All statuses</option>
            {Object.keys(STATUS_META).map((s) => (
              <option key={s} value={s}>
                {STATUS_META[s].label}
              </option>
            ))}
          </select>
          <select
            className="rounded-md border border-slate-300 px-3 py-2"
            data-testid="history-kind"
            onChange={(e) => setFilters({ ...filters, kind: e.target.value })}
            value={filters.kind}
          >
            <option value="">All types</option>
            <option value="walkin">Walk-in</option>
            <option value="invitation">Invitation</option>
            <option value="recurring">Recurring</option>
          </select>
          <input
            className="rounded-md border border-slate-300 px-3 py-2"
            data-testid="history-from"
            onChange={(e) => setFilters({ ...filters, date_from: e.target.value })}
            type="date"
            value={filters.date_from}
          />
          <input
            className="rounded-md border border-slate-300 px-3 py-2"
            data-testid="history-to"
            onChange={(e) => setFilters({ ...filters, date_to: e.target.value })}
            type="date"
            value={filters.date_to}
          />
          <Input
            data-testid="history-q"
            onChange={(e) => setFilters({ ...filters, q: e.target.value })}
            placeholder="Name / vehicle / unit"
            value={filters.q}
          />
          <Button data-testid="history-apply" onClick={applyFilters} variant="outline">
            <Search className="h-4 w-4" />
            Apply filters
          </Button>
        </div>
        <div className="mt-4 space-y-2" data-testid="history-results">
          {history.length === 0 ? (
            <p className="text-sm text-slate-500" data-testid="history-empty">
              Apply filters to view matching gate records.
            </p>
          ) : (
            history.map((visit) => (
              <div
                className="flex flex-wrap justify-between gap-2 border border-slate-100 bg-slate-50 p-3 text-sm"
                key={visit.visit_id}
              >
                <span>
                  {visit.visitor_name} · {visit.visitor_type} · Unit {visit.unit_number}
                </span>
                <span className="text-slate-500">
                  {visit.entry_event?.gate_name || visit.arrival_gate_name}
                  {visit.exit_event ? ` → ${visit.exit_event.gate_name}` : ""} ·{" "}
                  {STATUS_META[visit.status]?.label || visit.status}
                </span>
              </div>
            ))
          )}
        </div>
      </section>

      {/* Incidents */}
      <section
        className="mt-8 border border-slate-200 bg-white p-5"
        data-testid="gate-incident"
      >
        <h2 className="font-display text-lg font-semibold">Security incidents</h2>
        <div className="mt-3 grid gap-3 sm:grid-cols-3">
          <select
            className="rounded-md border border-slate-300 px-3 py-2"
            data-testid="incident-gate"
            onChange={(e) => setIncident({ ...incident, gate_id: e.target.value })}
            value={incident.gate_id}
          >
            <option value="">Gate / location</option>
            {data.gates.map((g) => (
              <option key={g.gate_id} value={g.gate_id}>
                {g.name}
              </option>
            ))}
          </select>
          <Input
            data-testid="incident-category"
            onChange={(e) => setIncident({ ...incident, category: e.target.value })}
            placeholder="Category"
            value={incident.category}
          />
          <select
            className="rounded-md border border-slate-300 px-3 py-2"
            data-testid="incident-severity"
            onChange={(e) => setIncident({ ...incident, severity: e.target.value })}
            value={incident.severity}
          >
            {INCIDENT_SEVERITIES.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </div>
        <Textarea
          className="mt-3"
          data-testid="gate-incident-description"
          onChange={(e) => setIncident({ ...incident, description: e.target.value })}
          placeholder="Internal security observation"
          value={incident.description}
        />
        <Button
          className="mt-3"
          data-testid="gate-create-incident"
          onClick={() =>
            run(async () => {
              await api.post("/society-gate/incidents", incident);
              setIncident({ gate_id: "", category: "Security", severity: "NORMAL", description: "" });
              loadIncidents();
            }, "Incident recorded")
          }
          size="sm"
          variant="outline"
        >
          <ShieldAlert className="h-4 w-4" />
          Record incident
        </Button>

        <div className="mt-5 space-y-2" data-testid="incident-list">
          {incidents.map((item) => (
            <article
              className="border border-slate-200 p-4"
              data-testid={`incident-row-${item.incident_id}`}
              key={item.incident_id}
            >
              <div className="flex flex-wrap justify-between gap-2">
                <strong className="text-sm">
                  {item.incident_number} · {item.category}
                </strong>
                <span className="rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-semibold">
                  {item.severity} · {item.status}
                </span>
              </div>
              <p className="mt-1 text-sm text-slate-600">{item.description}</p>
              <div className="mt-2 flex flex-wrap gap-2">
                {(INCIDENT_NEXT[item.status] || []).map((next) => (
                  <Button
                    data-testid={`incident-status-${item.incident_id}-${next}`}
                    key={next}
                    onClick={() =>
                      updateIncident(item.incident_id, { status: next }, `Marked ${next}`)
                    }
                    size="sm"
                    variant="outline"
                  >
                    {next}
                  </Button>
                ))}
              </div>
            </article>
          ))}
        </div>
      </section>
    </div>
  );
}
